import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const FormularioPublico = () => {
  const { linkPublico } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formulario, setFormulario] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [error, setError] = useState(null);
  
  // Datos del respondedor
  const [datosPersonales, setDatosPersonales] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: ''
  });
  
  // Respuestas a las preguntas
  const [respuestas, setRespuestas] = useState({});
  
  // Firma
  const [firmaData, setFirmaData] = useState(null);

  // Construir URL de API correctamente
  const BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BASE_URL}/api`;

  useEffect(() => {
    loadFormulario();
  }, [linkPublico]);

  const loadFormulario = async () => {
    try {
      const response = await axios.get(`${API}/public/formulario/${linkPublico}`);
      setFormulario(response.data.formulario);
      setPreguntas(response.data.preguntas);
      
      // Inicializar respuestas vacías
      const initialResponses = {};
      response.data.preguntas.forEach(p => {
        initialResponses[p.id] = '';
      });
      setRespuestas(initialResponses);
      
    } catch (error) {
      console.error('Error loading formulario:', error);
      if (error.response?.status === 404) {
        setError('Este formulario no existe o ya no está disponible.');
      } else {
        setError('Error al cargar el formulario. Por favor intenta más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRespuestaChange = (preguntaId, valor) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: valor
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar datos personales
    if (!datosPersonales.nombre || !datosPersonales.apellido || !datosPersonales.email) {
      toast.error('Por favor completa tu nombre, apellido y email');
      return;
    }
    
    // Validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(datosPersonales.email)) {
      toast.error('Por favor ingresa un email válido');
      return;
    }
    
    // Validar que todas las preguntas requeridas estén respondidas
    const preguntasSinResponder = preguntas.filter(p => {
      const resp = respuestas[p.id];
      return !resp || (typeof resp === 'string' && resp.trim() === '');
    });
    
    if (preguntasSinResponder.length > 0) {
      toast.error(`Por favor responde todas las preguntas. Faltan ${preguntasSinResponder.length} respuestas.`);
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Formatear respuestas
      const respuestasFormateadas = preguntas.map(p => ({
        pregunta_id: p.id,
        respuesta: respuestas[p.id]
      }));
      
      await axios.post(`${API}/public/formulario/${linkPublico}/responder`, {
        nombre: datosPersonales.nombre,
        apellido: datosPersonales.apellido,
        email: datosPersonales.email,
        telefono: datosPersonales.telefono || null,
        respuestas: respuestasFormateadas
      });
      
      setSubmitted(true);
      toast.success('¡Formulario enviado correctamente!');
      
    } catch (error) {
      console.error('Error submitting formulario:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Error al enviar el formulario. Por favor intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const renderPregunta = (pregunta, index) => {
    const { id, titulo, tipo, opciones } = pregunta;
    
    // Función helper para renderizar el título con HTML
    const renderTitulo = () => (
      <span>
        {index + 1}. <span dangerouslySetInnerHTML={{ __html: titulo }} /> <span className="text-red-500">*</span>
      </span>
    );
    
    switch (tipo) {
      case 'respuesta_corta':
        return (
          <div key={id} className="space-y-2">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <Input
              value={respuestas[id] || ''}
              onChange={(e) => handleRespuestaChange(id, e.target.value)}
              placeholder="Tu respuesta..."
              className="w-full"
            />
          </div>
        );
        
      case 'ensayo':
        return (
          <div key={id} className="space-y-2">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <Textarea
              value={respuestas[id] || ''}
              onChange={(e) => handleRespuestaChange(id, e.target.value)}
              placeholder="Escribe tu respuesta detallada aquí..."
              rows={4}
              className="w-full"
            />
          </div>
        );
        
      case 'opcion_multiple':
      case 'verdadero_falso':
        const opcionesLista = tipo === 'verdadero_falso' ? ['Sí', 'No'] : (opciones || []);
        return (
          <div key={id} className="space-y-3">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <RadioGroup
              value={respuestas[id] || ''}
              onValueChange={(value) => handleRespuestaChange(id, value)}
            >
              {opcionesLista.map((opcion, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <RadioGroupItem value={opcion} id={`${id}-${idx}`} />
                  <Label htmlFor={`${id}-${idx}`} className="cursor-pointer">
                    {opcion}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );
        
      case 'fecha':
        return (
          <div key={id} className="space-y-2">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <Input
              type="date"
              value={respuestas[id] || ''}
              onChange={(e) => handleRespuestaChange(id, e.target.value)}
              className="w-full max-w-xs"
            />
          </div>
        );
        
      case 'subir_foto':
        return (
          <div key={id} className="space-y-2">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              {respuestas[id] ? (
                <div className="space-y-3">
                  <div className="relative inline-block">
                    <img 
                      src={respuestas[id]} 
                      alt="Foto subida" 
                      className="max-w-full max-h-64 rounded-lg shadow-md mx-auto"
                    />
                    <button
                      type="button"
                      onClick={() => handleRespuestaChange(id, '')}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 shadow-md"
                    >
                      <i className="fas fa-times text-xs"></i>
                    </button>
                  </div>
                  <p className="text-sm text-green-600 font-medium">
                    <i className="fas fa-check-circle mr-1"></i>
                    Foto cargada correctamente
                  </p>
                </div>
              ) : (
                <label className="cursor-pointer block">
                  <div className="space-y-2">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                      <i className="fas fa-camera text-3xl text-blue-500"></i>
                    </div>
                    <p className="text-gray-700 font-medium">Haz clic para subir una foto</p>
                    <p className="text-sm text-gray-500">PNG, JPG o JPEG (máx. 5MB)</p>
                  </div>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          alert('La imagen no debe superar los 5MB');
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          handleRespuestaChange(id, event.target.result);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />
                </label>
              )}
            </div>
          </div>
        );
        
      case 'firma':
        return (
          <div key={id} className="space-y-2">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
              <Input
                value={respuestas[id] || ''}
                onChange={(e) => handleRespuestaChange(id, e.target.value)}
                placeholder="Escribe tu nombre completo como firma digital"
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-2">
                <i className="fas fa-info-circle mr-1"></i>
                Al escribir tu nombre, confirmas que la información proporcionada es verídica.
              </p>
            </div>
          </div>
        );
        
      default:
        return (
          <div key={id} className="space-y-2">
            <Label className="text-base font-medium">
              {renderTitulo()}
            </Label>
            <Input
              value={respuestas[id] || ''}
              onChange={(e) => handleRespuestaChange(id, e.target.value)}
              placeholder="Tu respuesta..."
              className="w-full"
            />
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p className="text-gray-600">Cargando formulario...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="text-red-500 text-6xl mb-4">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Formulario No Disponible</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8 text-center">
            <div className="text-green-500 text-6xl mb-4">
              <i className="fas fa-check-circle"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">¡Gracias!</h2>
            <p className="text-gray-600 mb-4">
              Tu formulario ha sido enviado correctamente.
            </p>
            <p className="text-sm text-gray-500">
              Recibirás una confirmación en tu correo electrónico.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Card className="shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
            <div className="flex items-center gap-4 mb-4">
              <img 
                src="/logo192.png" 
                alt="IBA Uruguay" 
                className="w-16 h-16 rounded-lg bg-white p-1"
                onError={(e) => e.target.style.display = 'none'}
              />
              <div>
                <h1 className="text-2xl font-bold">{formulario?.titulo}</h1>
                <p className="text-blue-100">Instituto Bíblico Apostólico del Uruguay</p>
              </div>
            </div>
            {formulario?.descripcion && (
              <p className="text-blue-100 border-t border-blue-400/30 pt-4 mt-4">
                {formulario.descripcion}
              </p>
            )}
          </div>
        </Card>

        {/* Formulario */}
        <form onSubmit={handleSubmit}>
          {/* Datos Personales */}
          <Card className="shadow-lg mb-6">
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="flex items-center text-lg">
                <i className="fas fa-user text-blue-600 mr-3"></i>
                Tus Datos Personales
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nombre">Nombre <span className="text-red-500">*</span></Label>
                  <Input
                    id="nombre"
                    value={datosPersonales.nombre}
                    onChange={(e) => setDatosPersonales({...datosPersonales, nombre: e.target.value})}
                    placeholder="Tu nombre"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="apellido">Apellido <span className="text-red-500">*</span></Label>
                  <Input
                    id="apellido"
                    value={datosPersonales.apellido}
                    onChange={(e) => setDatosPersonales({...datosPersonales, apellido: e.target.value})}
                    placeholder="Tu apellido"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
                  <Input
                    id="email"
                    type="email"
                    value={datosPersonales.email}
                    onChange={(e) => setDatosPersonales({...datosPersonales, email: e.target.value})}
                    placeholder="tu@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    value={datosPersonales.telefono}
                    onChange={(e) => setDatosPersonales({...datosPersonales, telefono: e.target.value})}
                    placeholder="+598 99 123 456"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preguntas */}
          <Card className="shadow-lg mb-6">
            <CardHeader className="bg-gray-50 border-b">
              <CardTitle className="flex items-center text-lg">
                <i className="fas fa-clipboard-list text-blue-600 mr-3"></i>
                Preguntas del Formulario
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {preguntas.map((pregunta, index) => renderPregunta(pregunta, index))}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={submitting}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 text-lg shadow-lg"
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Enviando...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane mr-2"></i>
                  Enviar Formulario
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8">
          <p>© {new Date().getFullYear()} Instituto Bíblico Apostólico del Uruguay</p>
        </div>
      </div>
    </div>
  );
};

export default FormularioPublico;
